import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button, Card, CardContent } from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useFinanceReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, StatusBadge } from '../components/catalog'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import {
  CAUSAL_FLAGS,
  CausalFlagsForm,
  CausalForm,
  causalPayload,
  emptyCausalFlags,
  emptyCausalForm,
  type CausalFlags,
  type CausalFormValue,
} from '../components/financeForms'
import { SignBadge, type Causal } from './Causals'

const TABS = ['summary', 'flags'] as const
type TabValue = (typeof TABS)[number]

/**
 * CausalDetail is one wallet causal, split into tabs: what it is (name, family,
 * group, the two optional references and the sign) and what it means (the 25
 * flags the platform reads).
 *
 * The id is never editable: every past wallet movement stores it. Retiring a
 * causal is the platform's switch and keeps the row — the movements that point
 * at it still have to resolve.
 */
export function CausalDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { causalFamilies, causalGroups, gameTypes, transactionTypes } = useFinanceReference()

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<CausalFormValue>(emptyCausalForm)
  const [editingFlags, setEditingFlags] = useState(false)
  const [flags, setFlags] = useState<CausalFlags>(emptyCausalFlags)

  const { data: causal } = useQuery({
    queryKey: ['causals', id],
    queryFn: async (): Promise<Causal> => {
      const res = await api.get(`/causals/${id}`)
      return res.data as Causal
    },
  })

  // Seed both forms from the loaded row (and re-seed after a save).
  useEffect(() => {
    if (!causal) return
    setForm({
      causalId: String(causal.causalId),
      causal: causal.causal,
      causalFamilyId: causal.causalFamilyId === null ? '' : String(causal.causalFamilyId),
      causalGroupId: String(causal.causalGroupId),
      gameTypeId: causal.gameTypeId === null ? '' : String(causal.gameTypeId),
      transactionTypeId:
        causal.transactionTypeId === null ? '' : String(causal.transactionTypeId),
      sign: String(causal.sign),
      flags: { ...emptyCausalFlags, ...causal.flags },
    })
    setFlags({ ...emptyCausalFlags, ...causal.flags })
  }, [causal])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['causals'] })
  const onError = (err: unknown) => setError(messageOf(err, t('causals.errors.save')))

  // Both saves send the whole row: the API replaces it wholesale, so the tab you
  // are not on must travel along untouched rather than be reset to its default.
  const save = useMutation({
    mutationFn: () =>
      api.put(`/causals/${id}`, {
        ...causalPayload(form, causal?.isDisabledByPlatform ?? false),
        flags,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const saveFlags = useMutation({
    mutationFn: () =>
      api.put(`/causals/${id}`, {
        ...causalPayload(form, causal?.isDisabledByPlatform ?? false),
        flags,
      }),
    onSuccess: () => {
      setEditingFlags(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      causal?.isDisabledByPlatform
        ? api.post(`/causals/${id}/restore`)
        : api.delete(`/causals/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  if (!causal) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')
  const activeFlags = CAUSAL_FLAGS.filter((f) => causal.flags?.[f]).length

  return (
    <div>
      <Link
        to="/causals"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('causals.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{causal.causal}</h2>
          <p className="text-sm text-muted-foreground">
            {t('causals.columns.id')} {causal.causalId}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SignBadge sign={causal.sign} />
          <StatusBadge disabled={causal.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {causal.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {causal.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('causals.tabs.summary')}</Tab>
          <Tab value="flags">
            {t('causals.tabs.flags')}
            <TabCount value={activeFlags} />
          </Tab>
        </TabsList>

        {/* ---- summary ---- */}
        <TabsPanel value="summary">
          {editing ? (
            <CausalForm
              value={form}
              onChange={setForm}
              onSubmit={() => save.mutate()}
              onCancel={() => {
                setEditing(false)
                setError('')
              }}
              families={causalFamilies}
              groups={causalGroups}
              gameTypes={gameTypes}
              transactionTypes={transactionTypes}
              isCreate={false}
              pending={save.isPending}
              error={error}
              submitLabel={t('common.save')}
            />
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[14rem_1fr] gap-x-6 gap-y-3 text-sm">
                    <SummaryRow label={t('causals.columns.id')}>
                      <span className="font-mono text-xs">{causal.causalId}</span>
                    </SummaryRow>
                    <SummaryRow label={t('causals.columns.name')}>{causal.causal}</SummaryRow>
                    <SummaryRow label={t('causals.columns.family')}>
                      {causal.causalFamily || '—'}
                    </SummaryRow>
                    <SummaryRow label={t('causals.columns.group')}>
                      {causal.causalGroup || '—'}
                    </SummaryRow>
                    <SummaryRow label={t('causals.columns.gameType')}>
                      {causal.gameType || '—'}
                    </SummaryRow>
                    <SummaryRow label={t('causals.columns.transactionType')}>
                      {causal.transactionType || '—'}
                    </SummaryRow>
                    <SummaryRow label={t('causals.columns.sign')}>
                      <SignBadge sign={causal.sign} />
                    </SummaryRow>
                    <SummaryRow label={t('common.columns.created')}>
                      {formatDate(causal.creationDate)}
                    </SummaryRow>
                    <SummaryRow label={t('paymentMethods.summary.lastModified')}>
                      {formatDate(causal.lastModificationDate)}
                    </SummaryRow>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        {/* ---- flags ---- */}
        <TabsPanel value="flags">
          <p className="mb-4 max-w-3xl text-sm text-muted-foreground">{t('causals.flagsHint')}</p>

          <Card className="max-w-5xl">
            <CardContent>
              <CausalFlagsForm
                value={flags}
                onChange={setFlags}
                onSubmit={() => saveFlags.mutate()}
                onCancel={() => {
                  setFlags({ ...emptyCausalFlags, ...causal.flags })
                  setEditingFlags(false)
                  setError('')
                }}
                readOnly={!editingFlags}
                pending={saveFlags.isPending}
                error={error}
                submitLabel={t('common.save')}
              />
            </CardContent>
          </Card>

          {canWrite && !editingFlags && (
            <Button
              className="mt-6"
              variant="outline"
              size="sm"
              onClick={() => setEditingFlags(true)}
            >
              <Pencil />
              {t('common.edit')}
            </Button>
          )}
        </TabsPanel>
      </Tabs>
    </div>
  )
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
